import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Box, CircularProgress } from "@mui/material";
import "./App.css";
import Login from "./Login";
import { Dashboard } from "./components/layout";
import {
  CompanyDetail,
  OrganizationDetail,
  UserDetail,
} from "./components/pages";
import { ToastProvider } from "./components/common";
import authService from "./services/authService";
import ModelDetails from "./components/ModelDetails/ModelDetails";

// Create a custom theme
const theme = createTheme({
  palette: {
    primary: {
      main: "#1976d2",
    },
    secondary: {
      main: "#dc004e",
    },
    background: {
      default: "#f5f5f5",
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h2: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 500,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          borderRadius: 8,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: "#f8f9fa",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
        },
      },
    },
  },
});

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already authenticated on app load
    const checkAuth = () => {
      if (authService.isAuthenticated()) {
        const user = authService.getUserData();
        setUserData(user);
        setIsAuthenticated(true);
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const handleLogin = (success) => {
    if (success) {
      const user = authService.getUserData();
      setUserData(user);
      setIsAuthenticated(true);
    }
  };

  const handleLogout = async () => {
    await authService.logout();
    setIsAuthenticated(false);
    setUserData(null);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
          }}
        >
          <CircularProgress size={60} />
        </Box>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastProvider>
        <Router>
          <div className="App">
            <Routes>
              <Route
                path="/"
                element={
                  <Dashboard onLogout={handleLogout} userData={userData} />
                }
              />
              <Route
                path="/companies"
                element={
                  <Dashboard onLogout={handleLogout} userData={userData} />
                }
              />
              <Route
                path="/projects"
                element={
                  <Dashboard onLogout={handleLogout} userData={userData} />
                }
              />
              <Route
                path="/promocodes"
                element={
                  <Dashboard onLogout={handleLogout} userData={userData} />
                }
              />
              <Route
                path="/users"
                element={
                  <Dashboard onLogout={handleLogout} userData={userData} />
                }
              />
              <Route
                path="/admin"
                element={
                  <Dashboard onLogout={handleLogout} userData={userData} />
                }
              />
              <Route
                path="/models"
                element={
                  <Dashboard onLogout={handleLogout} userData={userData} />
                }
              />
              <Route path="/models/:modelId" element={<ModelDetails />} />
              <Route
                path="/rules"
                element={
                  <Dashboard onLogout={handleLogout} userData={userData} />
                }
              />
              <Route
                path="/general"
                element={
                  <Dashboard onLogout={handleLogout} userData={userData} />
                }
              />
              <Route path="/company/:companyId" element={<CompanyDetail />} />
              <Route
                path="/project/:organizationId"
                element={<OrganizationDetail />}
              />
              <Route path="/user/:userId" element={<UserDetail />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </Router>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
